import { ImagePlus, Upload } from 'lucide-react'
import { ReactNode, useRef, useState } from 'react'

import type { Asset } from '@/api/common-types'
import { AssetLibraryPicker } from '@/components/asset-library-picker'
import { AssetImage } from '@/components/base/asset-image'
import { Button } from '@/components/base/button'
import { Container, ContainerHeader } from '@/components/base/container'
import { FileDropZone, isAcceptedImageFile } from '@/components/base/file-drop-zone'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { useAssetUpload } from '@/hooks/use-asset-upload'
import { useI18n } from '@/i18n/i18n-react'

const ACCEPTED_FORMATS = 'image/jpeg,image/jpg,image/png,image/webp,image/gif'

export type AssetImageManagerLabels = {
	title: string
	emptyState: string
	upload: string
	chooseFromLibrary: string
	remove: string
	libraryPickerTitle: string
	libraryPickerEmpty: string
	libraryPickerAllAttached: string
	uploadFailedToast: string
}

export function AssetImageManager({
	image,
	labels,
	onSetAsset,
	onRemove
}: {
	image: Asset | undefined
	labels: AssetImageManagerLabels
	onSetAsset: (assetUuid: string) => Promise<void>
	onRemove: () => Promise<void>
}) {
	const { L } = useI18n()
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [pickerOpen, setPickerOpen] = useState(false)
	const [isBusy, setIsBusy] = useState(false)
	const { upload, isUploading, progress } = useAssetUpload({ category: 'PRODUCT' })

	const disabled = isUploading || isBusy

	const handleSelectFile = () => fileInputRef.current?.click()

	const handleFile = async (files: FileList | null) => {
		const file = Array.from(files ?? []).find(isAcceptedImageFile)
		if (!file) {
			const rejected = files?.[0]
			if (rejected) toast.error(L.General.InvalidFileType(rejected.name, 'jpeg, png, webp, gif'))
			if (fileInputRef.current) fileInputRef.current.value = ''
			return
		}
		setIsBusy(true)
		try {
			const { asset } = await upload(file)
			await onSetAsset(asset.uuid)
		} catch (e: any) {
			toast.error(e?.message ?? labels.uploadFailedToast)
		} finally {
			setIsBusy(false)
			if (fileInputRef.current) fileInputRef.current.value = ''
		}
	}

	const handleSelectFromLibrary = async (asset: Asset) => {
		try {
			await onSetAsset(asset.uuid)
			setPickerOpen(false)
		} catch (e: any) {
			toast.error(e?.message ?? labels.uploadFailedToast)
		}
	}

	const handleRemove = async () => {
		setIsBusy(true)
		try {
			await onRemove()
		} catch (e: any) {
			toast.error(e?.message ?? L.General.Unknown())
		} finally {
			setIsBusy(false)
		}
	}

	let preview: ReactNode
	if (image) {
		preview = <AssetImage asset={image} className='aspect-square w-40 rounded-lg border border-ui-border-base' />
	} else if (isUploading) {
		preview = (
			<div className='flex w-40 flex-col gap-y-2'>
				<div className='h-1 w-full overflow-hidden rounded-full bg-ui-bg-component'>
					<div
						className='h-full bg-ui-fg-interactive transition-all'
						style={{ width: `${Math.max(progress, 5)}%` }}
					/>
				</div>
				<Text size='xsmall' className='text-ui-fg-muted'>
					{L.Product.Media.UploadingLabel({ percent: progress })}
				</Text>
			</div>
		)
	} else {
		preview = (
			<div className='flex w-40 flex-col items-center justify-center gap-y-2 rounded-lg border border-dashed border-ui-border-strong bg-ui-bg-subtle p-8'>
				<Text size='small' className='text-center text-ui-fg-muted'>
					{labels.emptyState}
				</Text>
			</div>
		)
	}

	return (
		<Container>
			<ContainerHeader
				heading={labels.title}
				rightSide={
					<div className='flex items-center gap-x-2'>
						<Button
							size='small'
							variant='secondary'
							onClick={() => setPickerOpen(true)}
							disabled={disabled}
						>
							<ImagePlus className='mr-2 h-4 w-4' />
							{labels.chooseFromLibrary}
						</Button>
						<Button size='small' variant='secondary' onClick={handleSelectFile} disabled={disabled}>
							<Upload className='mr-2 h-4 w-4' />
							{labels.upload}
						</Button>
						{image && (
							<Button size='small' variant='secondary' onClick={handleRemove} disabled={disabled}>
								{labels.remove}
							</Button>
						)}
					</div>
				}
			/>
			<input
				ref={fileInputRef}
				type='file'
				accept={ACCEPTED_FORMATS}
				hidden
				onChange={e => handleFile(e.target.files)}
			/>
			<FileDropZone
				className='mx-6 my-4 flex min-h-40 items-center'
				disabled={disabled}
				hint={L.General.DropImages()}
				onFiles={handleFile}
			>
				{preview}
			</FileDropZone>
			<AssetLibraryPicker
				open={pickerOpen}
				onOpenChange={setPickerOpen}
				attachedAssetUuids={new Set(image ? [image.uuid] : [])}
				onSelect={handleSelectFromLibrary}
				title={labels.libraryPickerTitle}
				emptyLabel={labels.libraryPickerEmpty}
				allAttachedLabel={labels.libraryPickerAllAttached}
			/>
		</Container>
	)
}
